import { useCallback, useEffect, useRef, useState } from 'react'
import type { AxiosError } from 'axios'

import { useApiClient } from '../../../helpers/apiClient.helper'
import { API_ENDPOINTS } from '../../../constants/api'
import { useDialog } from '../../shared/hooks/useDialog'
import { downloadUserDataExport } from '../helpers/downloadUserDataExport.helper'
import {
  getWebAnalyticsConsent,
  hydrateNativeAnalyticsConsent,
  setWebAnalyticsConsent,
  subscribeAnalyticsConsent,
} from '../helpers/webAnalyticsConsent.helper'
import {
  WEB_ANALYTICS_CONSENT_DENIED,
  WEB_ANALYTICS_CONSENT_GRANTED,
} from '../privacyConsent.constants'

interface PrivacyPreferencesResponse {
  success?: boolean
  data?: {
    marketingEmailOptIn?: boolean
  }
}

function parseRateLimitResetToDate(headers: Record<string, unknown> | undefined): Date | null {
  if (!headers) return null
  const raw = headers['ratelimit-reset']
  const value = Array.isArray(raw) ? raw[0] : raw
  if (typeof value !== 'string' && typeof value !== 'number') return null
  const seconds = Number(value)
  if (!Number.isFinite(seconds) || seconds <= 0) return null
  return new Date(Date.now() + seconds * 1000)
}

export function usePrivacyDataControls() {
  const apiClient = useApiClient()
  const apiClientRef = useRef(apiClient)
  const { showAlert } = useDialog()
  const [isReady, setIsReady] = useState(false)
  const [marketingEmailOptIn, setMarketingEmailOptIn] = useState(false)
  const [isUpdatingMarketing, setIsUpdatingMarketing] = useState(false)
  const [isExportingData, setIsExportingData] = useState(false)
  const [dataExportNextAvailableAt, setDataExportNextAvailableAt] = useState<Date | null>(null)
  const [webAnalyticsConsentEnabled, setWebAnalyticsConsentEnabled] = useState(false)

  useEffect(() => {
    apiClientRef.current = apiClient
  }, [apiClient])

  useEffect(() => {
    setWebAnalyticsConsentEnabled(getWebAnalyticsConsent() === WEB_ANALYTICS_CONSENT_GRANTED)
    void hydrateNativeAnalyticsConsent()
    return subscribeAnalyticsConsent(() => {
      setWebAnalyticsConsentEnabled(getWebAnalyticsConsent() === WEB_ANALYTICS_CONSENT_GRANTED)
    })
  }, [])

  useEffect(() => {
    let cancelled = false
    const client = apiClientRef.current
    if (!client) {
      setIsReady(true)
      return
    }
    client<PrivacyPreferencesResponse>(API_ENDPOINTS.users.privacyPreferences)
      .then((response) => {
        if (cancelled) return
        setMarketingEmailOptIn(Boolean(response.data?.data?.marketingEmailOptIn))
      })
      .catch(() => {
        if (cancelled) return
        // Backward-compatible fallback when backend hasn't deployed privacy-consent fields yet.
        setMarketingEmailOptIn(false)
      })
      .finally(() => {
        if (!cancelled) setIsReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const updateMarketingConsent = useCallback(async (nextValue: boolean) => {
    const client = apiClientRef.current
    if (!client) return
    setIsUpdatingMarketing(true)
    try {
      const response = await client<PrivacyPreferencesResponse>(API_ENDPOINTS.users.privacyPreferences, {
        method: 'PATCH',
        data: { marketingEmailOptIn: nextValue },
      })
      setMarketingEmailOptIn(Boolean(response.data?.data?.marketingEmailOptIn))
      await showAlert({
        title: 'Saved',
        message: nextValue
          ? 'Marketing email consent is now active.'
          : 'Marketing email consent was removed.',
        type: 'default',
      })
    } catch {
      await showAlert({
        title: 'Could not save',
        message: 'Please try again in a moment.',
        type: 'danger',
      })
    } finally {
      setIsUpdatingMarketing(false)
    }
  }, [showAlert])

  const requestDataExport = useCallback(async () => {
    const client = apiClientRef.current
    if (!client) return
    setIsExportingData(true)
    try {
      await downloadUserDataExport(client)
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string; error?: string }>
      const status = axiosError.response?.status
      const serverMessage =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        null
      if (status === 429) {
        setDataExportNextAvailableAt(parseRateLimitResetToDate(axiosError.response?.headers as Record<string, unknown> | undefined))
        await showAlert({
          title: 'Data export already requested',
          message: serverMessage || 'You already requested an export in the last 24 hours. Try again tomorrow.',
          type: 'default',
        })
        return
      }
      await showAlert({
        title: 'Could not download data',
        message: serverMessage || 'Try again later. You can request one export per 24 hours.',
        type: 'danger',
      })
    } finally {
      setIsExportingData(false)
    }
  }, [showAlert])

  const updateWebAnalyticsConsent = useCallback(async (nextValue: boolean) => {
    setWebAnalyticsConsent(nextValue ? WEB_ANALYTICS_CONSENT_GRANTED : WEB_ANALYTICS_CONSENT_DENIED)
    setWebAnalyticsConsentEnabled(nextValue)
    await showAlert({
      title: 'Saved',
      message: nextValue
        ? 'Analytics tracking is now enabled.'
        : 'Analytics tracking is now disabled.',
      type: 'default',
    })
  }, [showAlert])

  return {
    isReady,
    marketingEmailOptIn,
    isUpdatingMarketing,
    isExportingData,
    dataExportNextAvailableAt,
    webAnalyticsConsentEnabled,
    updateMarketingConsent,
    requestDataExport,
    updateWebAnalyticsConsent,
  }
}

